import {useMachine} from '@xstate/react/lib';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {Provider} from 'react-redux';
import {Store} from 'redux';
import {CurrentInterpreterProvider} from './CurrentInterpreterProvider';
import {IOuter} from './MachineProvider.types';
import {prependListener} from './prependListener';
import {useHistory} from './useHistory';
import {useMachineOptions} from './useMachineOptions';

export const MachineProvider: React.FunctionComponent<IOuter> = ({machine, initialContext, children}) => {
  const options = useMachineOptions();
  const [initialized, setInitialized] = useState(false);
  const storeRef = useRef<Store>();
  const [current, send, service] = useMachine(machine.withContext(initialContext), {
    ...options,
    devTools: true,
  });
  useHistory(service);
  useEffect(() => {
    const subscription = service.subscribe(() => {
      setInitialized(service.initialized);
    });
    return subscription.unsubscribe;
  }, [service, setInitialized]);

  if (!initialized) {
    return null;
  }

  if (!storeRef.current) {
    storeRef.current = {
      dispatch: (action) => {
        send(action);
        return action;
      },
      getState() {
        return service.state.context;
      },
      subscribe(listener: () => void) {
        // We need to call this handlers before other listener,
        // that located in useMachine to allow update of useSelector states.
        return prependListener(service as any, listener);
      },
      replaceReducer(): void {
        throw new Error('Not implemented');
      },
      [Symbol.observable]() {
        throw new Error('Not implemented');
      },
    };
  }
  return (
    <CurrentInterpreterProvider state={current} service={service}>
      <Provider store={storeRef.current}>{children}</Provider>
    </CurrentInterpreterProvider>
  );
};

MachineProvider.displayName = 'MachineProvider';
